import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../design/button";
import { Divider } from "../design/divider";
import { ErrorMessage, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Field } from "@headlessui/react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import logo from "../assets/logo.png";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { Link } from "../design/link";
import { Text } from "../design/text";

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  phoneNumber?: string;
  businessName?: string;
  businessWebsite?: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      // Call the server-side function to create the provider account
      const createProviderAccount = httpsCallable<
        { email: string; password: string; firstName: string; lastName: string },
        { success: boolean; userId: string; accountId: string }
      >(functions, "createProviderAccount");

      await createProviderAccount({
        email,
        password,
        firstName,
        lastName,
      });

      // Sign in the user after account creation
      await signInWithEmailAndPassword(auth, email, password);

      // Navigate to welcome page (checks for pending invites)
      navigate("/welcome");
    } catch (error: any) {
      console.error("Failed to create account", error);
      // Handle specific error messages from the server
      if (error.code === "functions/already-exists") {
        setErrors({ email: "An account with this email already exists" });
      } else if (error.code === "functions/invalid-argument") {
        setErrors({ email: error.message });
      } else {
        setErrors({ email: "Failed to create account. Please try again." });
      }
    }
    setIsSaving(false);
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First Name is required";
    if (!lastName) newErrors.lastName = "Last Name is required";
    if (!email) newErrors.email = "Email is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl dark:bg-blue-900" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 blur-3xl dark:bg-indigo-900" />
      </div>

      <div className="relative w-full max-w-lg px-4">
        <form
          method="post"
          className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50"
          onSubmit={handleSubmit}
        >
          {/* Back Button */}
          <button
            type="button"
            onClick={() => navigate("/signup")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>

          <img src={logo} alt="Logo" className="w-20 h-20 mx-auto mb-4" />
          <Fieldset>
            <Heading className="text-2xl font-bold text-center text-zinc-900 dark:text-white">
              Create Your Organization
            </Heading>
            <Text className="mt-2 text-center text-zinc-600 dark:text-zinc-400">
              Set up a new supervised visitation business. Staff and supervisors
              can be invited after you create your organization.
            </Text>
          <Divider className="my-6" />
          <FieldGroup>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={firstName}
                  placeholder="First Name"
                  onChange={(e) => setFirstName(e.target.value)}
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={lastName}
                  placeholder="Last Name"
                  onChange={(e) => setLastName(e.target.value)}
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                aria-label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            </Field>
          </FieldGroup>
          <Divider className="my-6" />
          <Button type="submit" className="w-full" disabled={isSaving}>
            {isSaving ? "Creating account..." : "Create Account"}
          </Button>
          <div className="my-6 text-center">
            <Text className="text-zinc-600 dark:text-zinc-400">
              Already have an account?{" "}
              <Link href="/login" className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400">
                Sign in
              </Link>
            </Text>
          </div>
        </Fieldset>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          By creating an account, you agree to our{" "}
          <a href="/terms" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
